import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

export default class DecoMagnet {
  constructor(el) {
    this.el = el;
    this.radius = 180;
    this.maxPush = 24;
    this.maxRotate = 3;
    this.easeOut = "power3.out";
    this.easeBack = "elastic.out(1, 0.5)";
    this.resetDuration = 0.9;
    this.moveDuration = 0.28;
    this.raf = null;
    this.pointer = { x: 0, y: 0 };
    this.active = false;
    this.current = { x: 0, y: 0, rotate: 0 };
    this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.canUseMagnet = window.matchMedia("(pointer:fine)").matches;

    if (this.prefersReducedMotion) return;

    this.revealOnScroll();
    if (!this.canUseMagnet) return;

    this.el.style.willChange = "transform, opacity";
    gsap.set(this.el, { transformOrigin: "50% 50%" });

    this.bindEvents();
  }

  revealOnScroll() {
    gsap.fromTo(
      this.el,
      {
        autoAlpha: 0,
        y: 70,
        scale: 0.52,
        rotateZ: gsap.utils.random(-16, 16),
      },
      {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        rotateZ: 0,
        duration: 1.2,
        ease: "elastic.out(1.15, 0.6)",
        scrollTrigger: {
          trigger: this.el,
          start: "clamp(top bottom)",
          once: true,
        },
      }
    );
  }

  bindEvents() {
    window.addEventListener("pointermove", (event) => {
      this.pointer.x = event.clientX;
      this.pointer.y = event.clientY;

      if (!this.active) {
        this.active = true;
      }

      if (!this.raf) {
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          this.update();
        });
      }
    });

    window.addEventListener("pointerleave", () => this.resetAll());
    window.addEventListener("blur", () => this.resetAll());
  }

  update() {
    const rect = this.el.getBoundingClientRect();
    const cx = rect.left + rect.width * 0.5;
    const cy = rect.top + rect.height * 0.5; 

    const dx = cx - this.pointer.x;
    const dy = cy - this.pointer.y;
    const dist = Math.hypot(dx, dy);

    if (dist < this.radius) {
      const power = 1 - dist / this.radius;
      const clampedDist = Math.max(dist, 1);
      const ux = dx / clampedDist;
      const uy = dy / clampedDist;
      const amount = this.maxPush * power;

      const rotate = (this.maxRotate * power) * -ux;
      this.animateTo(ux * amount, uy * amount, rotate, this.moveDuration, this.easeOut);
    } else {
      this.animateTo(0, 0, 0, this.resetDuration, this.easeBack);
    }
  }

  resetAll() {
    this.active = false;
    this.animateTo(0, 0, 0, this.resetDuration, this.easeBack);
  }

  animateTo(x, y, rotate, duration, ease) {
    if (
      Math.abs(this.current.x - x) < 0.1 &&
      Math.abs(this.current.y - y) < 0.1 &&
      Math.abs(this.current.rotate - rotate) < 0.1
    ) return;
    this.current.x = x;
    this.current.y = y;
    this.current.rotate = rotate;

    gsap.to(this.el, {
      x, 
      y,
      rotate,
      duration,
      ease,
      overwrite: true,
    });
  }
}
